<template>
  <b-container fluid>
    <h1>Remuneration Reconciliation</h1>
    <div v-if="loaded">
      <mio-filters label="Select country:" :options="RemunerationReconciliationData.filters" />
      <b-table-simple borderless outlined small>
        <b-tr><b-td><h3 class="text-secondary-mid">RATES</h3></b-td><b-td><h4><em>Reconciliation capped at 30 visits per month. Up to 5 of the 30 remunerated visits can be RC co-visits.</em></h4></b-td></b-tr>
        <template v-for="currency, country in RemunerationReconciliationData.currencies">
          <b-tr :data-filter="country">
            <b-td><strong>{{ country }}</strong></b-td>
            <b-td><span v-for="data, label in RemunerationReconciliationData.costs">{{ label }}: {{ data[country] }}{{ currency }}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span></b-td>
          </b-tr>
        </template>
      </b-table-simple>
      <div v-for="section in RemunerationReconciliationData.sections" :data-filter="section.title">
        <h2>{{ section.title }}</h2>
        <mio-output v-for="output, index in section.outputs" :key="index"
          :title="output.title"
          :tables="output.tables"
        />
      </div>
    </div>
    <h2 v-else class="loading-placeholder">Loading...</h2>
  </b-container>
</template>

<script>
import { mapActions } from 'vuex';
import { set, get, validateRow, formatPersonName } from '@/utils/MioHelpers';
import MioFilters from '@/components/MioFilters';
import MioOutput from '@/components/MioOutput';

export default {
  name: 'MioRemunerationReconciliation',
  components: {
    MioFilters,
    MioOutput,
  },
  data() {
    return {
      loaded: false,
    }
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions({
      GET_REMUNERATION: "mio/GET_REMUNERATION",
    }),
    async getData() {
      try {
        await this.GET_REMUNERATION();

        this.loaded = true;
      } catch (err) {
        console.error(err);
      }
    }
  },
  computed: {
    RemunerationReconciliationData: {
      get() {
        let working = this.$store.state.mio.remuneration;
        let output = {
          sections: [],
          method: "",
          messages: [],
        };
        let maxPayableVisitsPerMonth = 30;
        let maxAllowedCovisitsPerMonth = 5;
        let currencies = {
          Malawi: 'MK',
          Namibia: 'N$',
          Zambia: 'ZK',
          Uganda: 'UGX',
        };
        let cols = {
          modulesDelivered: 'modules delivered',
          supportGroups: 'support groups held',
          moduleVisits: 'module visits',
          maintenanceVisits: 'maintenance visits',
          covisits: 'RC co-visits',
          totalVisits: 'total visits',
          remuneration: 'remuneration',
        };
        let costs = {
          modules: {
            Malawi: 22000,
            Namibia: 350,
            Zambia: 360,
            Uganda: 111000,
          },
          groups: {
            Malawi: 11000,
            Namibia: 150,
            Zambia: 180,
            Uganda: 50000,
          },
          visits: {
            Malawi: 7000,
            Namibia: 150,
            Zambia: 120,
            Uganda: 35000,
          }
        };

        // Format for output
        output.sections = Object.keys(working.coordinators).map(country => ({
          title: country,
          outputs: Object.keys(working.coordinators[country]).map(coordinatorID => ({
            tables: [{
              title: working.coordinators[country][coordinatorID],
              firstRowHeader: 'Trainers',
              cols: Object.keys(cols).map(index => cols[index]),
              rows: working.data[coordinatorID] && Object.keys(working.data[coordinatorID].trainers).map(trainerID => ({
                title: working.trainers[trainerID],
                subRows: Object.keys(working.data[coordinatorID].trainers[trainerID].months).sort().reduce((rows, month, index, rawData) => {

                  // basic stats
                  let stats = ['modulesDelivered', 'supportGroups', 'moduleVisits', 'maintenanceVisits'].map(col => working.data[coordinatorID].trainers[trainerID].months[month][col]);

                  // RC covisits
                  let covisits = working.data[coordinatorID].trainers[trainerID].months[month].covisits;
                  stats.push(covisits > 5 ? `<span class="badge badge-warning">${covisits}</em>` : covisits);

                  // sum visits
                  let totalVisits = (parseInt(covisits) || 0) + (parseInt(working.data[coordinatorID].trainers[trainerID].months[month]['moduleVisits']) || 0) + (parseInt(working.data[coordinatorID].trainers[trainerID].months[month]['maintenanceVisits']) || 0);
                  stats.push(totalVisits > maxPayableVisitsPerMonth ? `<span class="badge badge-warning">${totalVisits}</em>` : totalVisits);

                  // calculate remuneration

                  // totalVisits already includes covisits, so adding min(covisits, 5) on top
                  // paid them twice. The stated rule is a ceiling: of the 30 remunerated
                  // visits, at most 5 may be co-visits.
                  let ownVisits = (parseInt(working.data[coordinatorID].trainers[trainerID].months[month]['moduleVisits']) || 0) + (parseInt(working.data[coordinatorID].trainers[trainerID].months[month]['maintenanceVisits']) || 0);
                  let payableCovisits = Math.min(parseInt(covisits) || 0, maxAllowedCovisitsPerMonth);
                  let totalPayableVisits = Math.min(ownVisits + payableCovisits, maxPayableVisitsPerMonth);

                  let remuneration =
                    ((parseInt(working.data[coordinatorID].trainers[trainerID].months[month]['modulesDelivered']) || 0) * costs.modules[country]) +
                    ((parseInt(working.data[coordinatorID].trainers[trainerID].months[month]['supportGroups']) || 0) * costs.groups[country]) +
                    (totalPayableVisits * costs.visits[country]);
                  stats.push(`${remuneration} ${currencies[country]}`);

                  // save remuneration to running total for the year
                  const year = month.substring(0, 4);
                  set(working, `data.${coordinatorID}.trainers.${trainerID}.years.${year}.remuneration`, remuneration, 'add');

                  // output
                  rows.push({
                    title: month,
                    data: stats,
                  });

                  // add totals per year where relevant
                  if (index < (rawData.length - 1) && year != rawData[index + 1].substring(0, 4)) {

                    // basic stats
                    let stats = ['modulesDelivered', 'supportGroups', 'moduleVisits', 'maintenanceVisits', 'covisits'].map(col => working.data[coordinatorID].trainers[trainerID].years[year][col]);

                    // sum visits
                    let totalVisits = (parseInt(working.data[coordinatorID].trainers[trainerID].years[year].moduleVisits) || 0) + (parseInt(working.data[coordinatorID].trainers[trainerID].years[year].maintenanceVisits) || 0);
                    stats.push(totalVisits);

                    // remuneration
                    stats.push(`${working.data[coordinatorID].trainers[trainerID].years[year].remuneration} ${currencies[country]}`);

                    // output
                    rows.push({
                      title: `total for ${year}`,
                      class: 'bg-secondary-light font-italic font-weight-normal',
                      data: stats,
                    });
                  }
                  return rows;
                }, []).reverse(),
              })),
            }]
          }))
        }));

        // Add country filters
        output.filters = Object.keys(working.coordinators).sort();

        // Add rates information
        output.currencies = currencies;
        output.costs = costs;

        // console.log(working);
        // console.log(output);
        return output;
      }
    }
  },
};
</script>
